/**
 * BoatWise Academy — private learning experience shell (Phase 0–E).
 * Only rendered when user.academy_enabled is true.
 */
import React, { useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  AcademicCapIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  CheckBadgeIcon,
} from '@heroicons/react/24/outline';
import { theme } from '../../styles/theme';
import api from '../../config/api';

const Page = styled.div`
  min-height: calc(100vh - 80px);
  background:
    radial-gradient(ellipse at top left, rgba(14, 165, 233, 0.12), transparent 45%),
    radial-gradient(ellipse at bottom right, rgba(30, 58, 138, 0.18), transparent 50%),
    linear-gradient(160deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem 1.25rem 3rem;
`;

const Shell = styled.div`
  max-width: 960px;
  margin: 0 auto;
`;

const Eyebrow = styled.p`
  margin: 0 0 0.5rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.colors.secondary.main};
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-size: clamp(1.75rem, 4vw, 2.35rem);
  font-weight: 800;
  color: ${theme.colors.text.primary};
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.65rem;

  svg {
    width: 1.85rem;
    height: 1.85rem;
    color: ${theme.colors.secondary.main};
    flex-shrink: 0;
  }
`;

const Lead = styled.p`
  margin: 0 0 2rem;
  max-width: 42rem;
  font-size: 1.05rem;
  line-height: 1.55;
  color: ${theme.colors.text.secondary};
`;

const ContinueCard = styled.section`
  margin: 0 0 1.75rem;
  padding: 1.35rem 1.5rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  border-left: 4px solid ${theme.colors.secondary.main};
`;

const ContinueLabel = styled.p`
  margin: 0 0 0.35rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${theme.colors.secondary.main};
`;

const ContinueTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const ContinueReason = styled.p`
  margin: 0 0 1rem;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${theme.colors.text.secondary};
`;

const ContinueMeta = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
`;

const ContinueActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  border: none;
  border-radius: ${theme.borderRadius.md};
  padding: 0.65rem 1.1rem;
  background: ${theme.colors.secondary.main};
  color: #fff;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const GhostButton = styled.button`
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.4rem 0.2rem;

  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

const HealthStrip = styled.section`
  margin: 0 0 1.75rem;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 0.65rem;
`;

const HealthCell = styled.div`
  padding: 0.75rem 0.85rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
`;

const HealthValue = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${theme.colors.text.primary};
`;

const HealthLabel = styled.div`
  margin-top: 0.15rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.colors.text.secondary};
`;

const SecondaryList = styled.ul`
  margin: 0 0 1.75rem;
  padding: 0;
  list-style: none;
`;

const SecondaryItem = styled.li`
  margin: 0 0 0.5rem;
  padding: 0.75rem 0.9rem;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};

  strong {
    color: ${theme.colors.text.primary};
    font-weight: 650;
  }
`;

const ProgramCard = styled(Link)`
  display: block;
  text-decoration: none;
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem 1.6rem;
  transition: border-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${theme.colors.secondary.main};
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.08);
  }
`;

const ProgramTitle = styled.h2`
  margin: 0 0 0.35rem;
  font-size: 1.25rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const ProgramSubtitle = styled.p`
  margin: 0 0 1rem;
  font-size: 0.95rem;
  color: ${theme.colors.text.secondary};
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.28rem 0.65rem;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.secondary};

  svg {
    width: 0.9rem;
    height: 0.9rem;
  }
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
  padding: 0;
  border: none;
  background: transparent;
  color: ${theme.colors.secondary.main};
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;

  svg {
    width: 1rem;
    height: 1rem;
  }
`;

const PillarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-top: 1.5rem;
`;

const Pillar = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.15rem 1.25rem;
`;

const PillarTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-size: 1rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const PillarBody = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${theme.colors.text.secondary};
`;

const LessonTest = styled.blockquote`
  margin: 1.75rem 0 0;
  padding: 1.1rem 1.25rem;
  border-left: 3px solid ${theme.colors.accent.main};
  background: ${theme.colors.background.paper};
  border-radius: 0 ${theme.borderRadius.md} ${theme.borderRadius.md} 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: ${theme.colors.text.primary};
  font-style: italic;
`;

const Status = styled.p`
  margin: 2rem 0 0;
  font-size: 0.9rem;
  color: ${theme.colors.text.secondary};
`;

const ErrorText = styled.p`
  color: ${theme.colors.error?.main || '#dc2626'};
`;

const SectionEyebrow = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${theme.colors.text.secondary};
`;

const ChatLog = styled.div`
  margin: 1rem 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-height: 52vh;
  overflow-y: auto;
`;

const Bubble = styled.div`
  align-self: ${(p) => (p.$role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 85%;
  padding: 0.75rem 0.95rem;
  border-radius: ${theme.borderRadius.md};
  background: ${(p) =>
    p.$role === 'user' ? theme.colors.secondary.main : theme.colors.background.paper};
  color: ${(p) => (p.$role === 'user' ? '#fff' : theme.colors.text.primary)};
  border: 1px solid
    ${(p) => (p.$role === 'user' ? 'transparent' : theme.colors.border.primary)};
  font-size: 0.95rem;
  line-height: 1.45;
  white-space: pre-wrap;
`;

const ChatForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-top: 1rem;
`;

const ChatInput = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  padding: 0.75rem 0.9rem;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  font: inherit;
  resize: vertical;
`;

const CapabilityHint = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
`;

function formatCalibration(value) {
  switch (value) {
    case 'needs_attention':
      return 'Needs attention';
    case 'catching_up':
      return 'Catching up';
    case 'stable':
      return 'Stable';
    default:
      return '—';
  }
}

function AcademyHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [actionBusy, setActionBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/academy');
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Unable to load Academy');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const continueLearning = data?.continue_learning;
  const primary = continueLearning?.primary;
  const health = continueLearning?.health;
  const secondary = continueLearning?.secondary || [];

  async function handleContinue() {
    if (!primary) return;
    setActionBusy(true);
    try {
      if (primary.history_id) {
        await api.post('/api/academy/me/continue/follow', { history_id: primary.history_id });
      }
      const sessionRes = await api.post('/api/academy/teaching/sessions', {
        learning_mode: 'unrestricted_learning',
      });
      const sessionId = sessionRes.data?.session?.id;
      if (sessionId) {
        navigate(`/academy/teach/${sessionId}`);
        return;
      }
      const href = primary.target?.href || '/academy/programs/recreational-marine-surveyor';
      navigate(href);
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to start Continue Learning');
    } finally {
      setActionBusy(false);
    }
  }

  async function handleDismiss() {
    if (!primary?.history_id) return;
    setActionBusy(true);
    try {
      await api.post('/api/academy/me/continue/dismiss', { history_id: primary.history_id });
      const res = await api.get('/api/academy/me/continue');
      setData((prev) => ({ ...prev, continue_learning: res.data }));
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to update recommendation');
    } finally {
      setActionBusy(false);
    }
  }

  if (error && !data) {
    return (
      <Page>
        <Shell>
          <ErrorText>{error}</ErrorText>
        </Shell>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page>
        <Shell>
          <Lead>Loading Academy…</Lead>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell>
        <Eyebrow>Private learning</Eyebrow>
        <Title>
          <AcademicCapIcon />
          {data.title}
        </Title>
        <Lead>
          {data.tagline} {data.mission}
        </Lead>

        {primary && (
          <ContinueCard>
            <ContinueLabel>Continue Learning</ContinueLabel>
            <ContinueTitle>{primary.title}</ContinueTitle>
            <ContinueReason>{primary.reason_text}</ContinueReason>
            {primary.estimated_minutes != null && (
              <ContinueMeta>About {primary.estimated_minutes} minutes</ContinueMeta>
            )}
            <ContinueActions>
              <PrimaryButton type="button" onClick={handleContinue} disabled={actionBusy}>
                Continue Learning
                <ArrowRightIcon />
              </PrimaryButton>
              {primary.history_id && (
                <GhostButton type="button" onClick={handleDismiss} disabled={actionBusy}>
                  Not now
                </GhostButton>
              )}
            </ContinueActions>
          </ContinueCard>
        )}

        {health && (
          <>
            <SectionEyebrow>Learning health</SectionEyebrow>
            <HealthStrip>
              <HealthCell>
                <HealthValue>{health.strong ?? 0}</HealthValue>
                <HealthLabel>Strong</HealthLabel>
              </HealthCell>
              <HealthCell>
                <HealthValue>{health.competencies_growing}</HealthValue>
                <HealthLabel>Growing</HealthLabel>
              </HealthCell>
              <HealthCell>
                <HealthValue>{health.competencies_needing_attention}</HealthValue>
                <HealthLabel>Needs attention</HealthLabel>
              </HealthCell>
              <HealthCell>
                <HealthValue>{health.at_risk ?? 0}</HealthValue>
                <HealthLabel>At risk</HealthLabel>
              </HealthCell>
              <HealthCell>
                <HealthValue>{health.recovering ?? 0}</HealthValue>
                <HealthLabel>Recovering</HealthLabel>
              </HealthCell>
              <HealthCell>
                <HealthValue>{health.critical_misconceptions}</HealthValue>
                <HealthLabel>Critical misconceptions</HealthLabel>
              </HealthCell>
              <HealthCell>
                <HealthValue>{formatCalibration(health.confidence_calibration)}</HealthValue>
                <HealthLabel>Confidence</HealthLabel>
              </HealthCell>
            </HealthStrip>
          </>
        )}

        {secondary.length > 0 && (
          <>
            <SectionEyebrow>Also useful</SectionEyebrow>
            <SecondaryList>
              {secondary.map((item) => (
                <SecondaryItem key={item.recommendation_key}>
                  <strong>{item.title}</strong>
                  {' — '}
                  {item.reason_text}
                </SecondaryItem>
              ))}
            </SecondaryList>
          </>
        )}

        {(data.programs || []).map((program) => (
          <ProgramCard key={program.id} to={`/academy/programs/${program.slug}`}>
            <ProgramTitle>{program.title}</ProgramTitle>
            <ProgramSubtitle>{program.subtitle}</ProgramSubtitle>
            <MetaRow>
              <Chip>
                <CheckBadgeIcon />
                {program.status === 'foundation' ? 'Foundation ready' : program.status}
              </Chip>
              {(program.audience || []).slice(0, 3).map((a) => (
                <Chip key={a}>{a}</Chip>
              ))}
            </MetaRow>
          </ProgramCard>
        ))}
      </Shell>
    </Page>
  );
}

function AcademyProgram() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [program, setProgram] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/academy/programs/${slug}`);
        if (!cancelled) setProgram(res.data);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Program not found');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error) {
    return (
      <Page>
        <Shell>
          <BackLink type="button" onClick={() => navigate('/academy')}>
            <ArrowLeftIcon /> Back to Academy
          </BackLink>
          <ErrorText>{error}</ErrorText>
        </Shell>
      </Page>
    );
  }

  if (!program) {
    return (
      <Page>
        <Shell>
          <Lead>Loading program…</Lead>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell>
        <BackLink type="button" onClick={() => navigate('/academy')}>
          <ArrowLeftIcon /> Back to Academy
        </BackLink>
        <Eyebrow>Program</Eyebrow>
        <Title>{program.title}</Title>
        <Lead>{program.outcome}</Lead>

        <MetaRow>
          <Chip>
            <CheckBadgeIcon />
            {program.status === 'foundation' ? 'Foundation ready' : program.status}
          </Chip>
          {(program.audience || []).map((a) => (
            <Chip key={a}>{a}</Chip>
          ))}
        </MetaRow>

        <PillarGrid>
          {(program.competencyPillars || []).map((pillar) => (
            <Pillar key={pillar.id}>
              <PillarTitle>{pillar.title}</PillarTitle>
              <PillarBody>{pillar.capability}</PillarBody>
            </Pillar>
          ))}
        </PillarGrid>

        {program.lessonTest && (
          <LessonTest>
            Every lesson must answer: “{program.lessonTest}”
          </LessonTest>
        )}

        <Status>
          Curriculum modules publish into this program as they are written. This overview is your home
          base for the surveyor track.
        </Status>
      </Shell>
    </Page>
  );
}

function TeachingSession() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [activityTitle, setActivityTitle] = useState('');
  const [capability, setCapability] = useState(null);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/api/academy/teaching/sessions/${sessionId}`);
        if (cancelled) return;
        setMessages(res.data.messages || []);
        const pack = res.data.session?.context_pack;
        setActivityTitle(pack?.activity?.title || 'Teaching session');
        setCapability(res.data.session?.capability || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || 'Unable to load teaching session');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  async function handleSend(e) {
    e.preventDefault();
    const text = draft.trim();
    if (!text || busy) return;
    setBusy(true);
    setError(null);
    setDraft('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    try {
      const res = await api.post(`/api/academy/teaching/sessions/${sessionId}/messages`, {
        message: text,
      });
      if (res.data.capability) setCapability(res.data.capability);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: res.data.reply, capability: res.data.capability },
      ]);
    } catch (err) {
      setError(err?.response?.data?.error || 'Unable to send message');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Page>
      <Shell>
        <BackLink type="button" onClick={() => navigate('/academy')}>
          <ArrowLeftIcon /> Back to Academy
        </BackLink>
        <Eyebrow>Adaptive teaching</Eyebrow>
        <Title>{activityTitle}</Title>
        {capability && (
          <CapabilityHint>
            Capability: {String(capability).replace(/_/g, ' ')}
          </CapabilityHint>
        )}
        {error && <ErrorText>{error}</ErrorText>}
        <ChatLog>
          {messages.map((m, idx) => (
            <Bubble key={`${m.role}-${idx}-${m.id || idx}`} $role={m.role}>
              {m.content}
            </Bubble>
          ))}
        </ChatLog>
        <ChatForm onSubmit={handleSend}>
          <ChatInput
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask a question or explain what you understand…"
            disabled={busy}
          />
          <ContinueActions>
            <PrimaryButton type="submit" disabled={busy || !draft.trim()}>
              {busy ? 'Teaching…' : 'Send'}
              <ArrowRightIcon />
            </PrimaryButton>
          </ContinueActions>
        </ChatForm>
      </Shell>
    </Page>
  );
}

export default function Academy() {
  return (
    <Routes>
      <Route index element={<AcademyHome />} />
      <Route path="programs/:slug" element={<AcademyProgram />} />
      <Route path="teach/:sessionId" element={<TeachingSession />} />
      <Route path="*" element={<AcademyHome />} />
    </Routes>
  );
}
